import { UI } from '@/Components/UI';
import { useApi, useConfig, useDynamicInputs } from '@/hooks';
import { MeasuringUnit } from '@/types';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

type IngredientsFormProps = {
    groupIndex: number;
}

export const IngredientsForm = ({ groupIndex }: IngredientsFormProps) => {
    const { t } = useTranslation(['recipes']);
    const { SearchService } = useApi();
    const { measuringUnits } = useConfig();
    const { addValue, removeValue, values } = useDynamicInputs(`ingredientGroups.${groupIndex}.ingredients`, {
        amount: 0,
        measuringUnit: 'UNKNOWN' as MeasuringUnit,
        food: { name: '' }
    });

    useEffect(() => {
        if (!values || values.length === 0) {
            addValue();
        }
    }, [values]);

    const getSuggestions = async (value: string | undefined) => {
        if (!value || value.length < 3) {
            return Promise.resolve(undefined);
        }

        return SearchService
            .searchFood(value)
            .then((response) => {
                return response.data.map((food) => ({
                    label: food.name,
                    value: food.name
                }));
            })
            .catch(() => {
                return undefined;
            });
    };

    const unitOptions = Object
        .values(measuringUnits)
        .flatMap((typeInfo) =>
            typeInfo
                .units
                .map((unit) => ({
                    label: t(`ingredients.units.${unit.unit}`) ?? unit.unit,
                    value: unit.unit,
                }))
        );

    return (
        <>
            <UI.Heading level={2}>
                {t('form.ingredients.heading')}
            </UI.Heading>

            <div className={'flex flex-col gap-2 mb-2'}>
                {values?.map((_, index) => (
                    <div
                        key={index}
                        className={'grid grid-cols-2 gap-x-3 px-4 py-2 rounded-lg border border-primary'}>
                        <div className={'col-span-2 text-end'}>
                            <UI.Button
                                buttonStyle={'tonal'}
                                type={'button'}
                                onClick={() => removeValue(index)}>
                                <UI.Icon name={'delete'} />
                            </UI.Button>
                        </div>

                        <UI.Form.Input
                            name={`ingredientGroups.${groupIndex}.ingredients.${index}.amount`}
                            label={t('form.ingredients.amount')} />

                        <UI.Form.Select
                            name={`ingredientGroups.${groupIndex}.ingredients.${index}.measuringUnit`}
                            label={t('form.ingredients.unit')}
                            options={unitOptions} />

                        <UI.Form.Autocomplete
                            groupClassName={'col-span-2'}
                            name={`ingredientGroups.${groupIndex}.ingredients.${index}.food.name`}
                            label={t('form.ingredients.ingredient')}
                            getSuggestions={(value) => getSuggestions(String(value))} />
                    </div>
                ))}

                <UI.Button
                    buttonStyle={'tonal'}
                    type={'button'}
                    onClick={addValue}>
                    <UI.Icon name={'add'} />
                </UI.Button>
            </div>
        </>
    );
};
